import { createClerkSupabaseClient } from '@/lib/supabase'
import { StorageService } from './storageService'
import type { Database } from '@/interfaces/database.types'
import type { ServiceAuthProps } from '@/interfaces/serviceAuth'

type FileRow = Database['public']['Tables']['files']['Row']
type FileInsert = Database['public']['Tables']['files']['Insert']

export interface ProjectFile {
  id: string
  name: string
  originalName: string
  projectId: string
  organizationId: string
  filePath: string
  fileSize: number
  uploadedBy: string
  uploadedAt: string
  metadata?: Database['public']['Tables']['files']['Row']['metadata']
  downloadUrl?: string // Add for signed download URLs
}

// Convert database row to our interface
function mapFileRowToProjectFile(row: FileRow): ProjectFile {
  return {
    id: row.id,
    name: row.name,
    originalName: row.original_name,
    projectId: row.project_id,
    organizationId: row.organization_id,
    filePath: row.file_path,
    fileSize: row.file_size,
    uploadedBy: row.uploaded_by,
    uploadedAt: row.uploaded_at || new Date().toISOString(),
    metadata: row.metadata
  }
}

export interface DuplicateCheckResult {
  isDuplicate: boolean
  existingFile?: ProjectFile
  duplicateType: 'name' | 'name_size' | 'content_hash'
}

export interface UploadOptions {
  skipDuplicateCheck?: boolean
  overwriteDuplicates?: boolean
  duplicateDetectionLevel?: 'name' | 'name_size' | 'content_hash'
}

/**
 * Enhanced FileService that coordinates storage and database operations
 * Handles complete file lifecycle: upload, storage, database records, download, delete
 */
export class FileService {
  private storageService: StorageService
  private sessionProps: ServiceAuthProps

  constructor(sessionProps: ServiceAuthProps) {
    this.sessionProps = sessionProps
    this.storageService = new StorageService(sessionProps)
  }

  /**
   * Check if a file already exists in the project
   */
  async checkForDuplicates(
    file: File, 
    projectId: string,
    level: 'name' | 'name_size' | 'content_hash' = 'name_size'
  ): Promise<DuplicateCheckResult> {
    const existingFiles = await this.getProjectFiles(projectId)
    
    for (const existingFile of existingFiles) {
      // Level 1: Name only
      if (level === 'name' && existingFile.originalName === file.name) {
        return { 
          isDuplicate: true, 
          existingFile, 
          duplicateType: 'name' 
        }
      }
      
      // Level 2: Name + Size
      if (level === 'name_size' && 
          existingFile.originalName === file.name && 
          existingFile.fileSize === file.size) {
        return { 
          isDuplicate: true, 
          existingFile, 
          duplicateType: 'name_size' 
        }
      }
      
      // Level 3: Content hash (most reliable but slower)
      if (level === 'content_hash' && 
          existingFile.originalName === file.name) {
        const existingHash = await this.getFileContentHash(existingFile.id)
        const newFileHash = await this.calculateFileHash(file)
        
        if (existingHash === newFileHash) {
          return { 
            isDuplicate: true, 
            existingFile, 
            duplicateType: 'content_hash' 
          }
        }
      }
    }
    
    return { isDuplicate: false, duplicateType: level }
  }

  /**
   * Calculate SHA-256 hash of file content
   */
  private async calculateFileHash(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Get content hash for existing file
   */
  private async getFileContentHash(fileId: string): Promise<string> {
    // Check if hash exists in metadata
    const file = await this.getFileById(fileId)
    const metadata = file.metadata as { contentHash?: string } | null | undefined
    if (metadata?.contentHash) {
      return metadata.contentHash
    }
    
    // Calculate and cache hash
    const { content } = await this.getFileContentForParsing(fileId)
    
    // Handle both string (CSV) and ArrayBuffer (XLSX) content
    let data: ArrayBuffer
    if (content instanceof ArrayBuffer) {
      // XLSX files - already binary, use directly
      data = content
    } else {
      // CSV files - convert string to binary for hashing
      const encoder = new TextEncoder()
      const encoded = encoder.encode(content)
      // Create a new ArrayBuffer from the Uint8Array
      data = new ArrayBuffer(encoded.length)
      new Uint8Array(data).set(encoded)
    }
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    
    // Cache hash in metadata
    const existingMetadata = (file.metadata as Record<string, any>) || {}
    await this.updateFileMetadata(fileId, { 
      ...existingMetadata, 
      contentHash: hash 
    })
    
    return hash
  }

  /**
   * Complete file upload: storage upload + database record creation
   * Now with duplicate detection
   */
  async uploadFile(
    file: File, 
    projectId: string, 
    options: UploadOptions = {}
  ): Promise<{ success: boolean; file?: ProjectFile; duplicate?: ProjectFile; message?: string }> {
    const {
      skipDuplicateCheck = false,
      overwriteDuplicates = false,
      duplicateDetectionLevel = 'name_size'
    } = options

    try {
      // 1. Check for duplicates first (unless skipped)
      if (!skipDuplicateCheck) {
        const duplicateCheck = await this.checkForDuplicates(file, projectId, duplicateDetectionLevel)
        
        if (duplicateCheck.isDuplicate) {
          if (overwriteDuplicates && duplicateCheck.existingFile) {
            // Option B: Overwrite existing file
            return await this.overwriteExistingFile(file, duplicateCheck.existingFile)
          } else {
            // Option A: Ignore duplicate
            return {
              success: false,
              duplicate: duplicateCheck.existingFile,
              message: `File "${file.name}" already exists in this project (${duplicateCheck.duplicateType} match)`
            }
          }
        }
      }

      // 2. Proceed with normal upload (no duplicate found)
      const project = await this.getProjectById(projectId)
      const storagePath = StorageService.generateStoragePath(projectId, file.name, project.organization_id)
      const uploadResult = await this.storageService.uploadFile(file, storagePath)

      // 3. Create database record with content hash for future duplicate detection
      const contentHash = await this.calculateFileHash(file)
      
      const projectFile = await this.createFileRecord({
        name: file.name,
        originalName: file.name,
        projectId,
        organizationId: project.organization_id,
        filePath: uploadResult.path,
        fileSize: file.size,
        uploadedBy: this.sessionProps.userId,
        metadata: {
          storageId: uploadResult.id,
          fullPath: uploadResult.fullPath,
          contentHash,
          uploadedAt: new Date().toISOString()
        }
      })

      return { success: true, file: projectFile }

    } catch (error) {
      console.error('File upload failed:', error)
      throw error instanceof Error ? error : new Error('Upload failed')
    }
  }

  /**
   * Get all files for a project with optional download URLs
   */
  async getProjectFiles(projectId: string, includeDownloadUrls = false): Promise<ProjectFile[]> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const { data, error } = await supabase
      .from('files')
      .select('*')
      .eq('project_id', projectId)
      .order('uploaded_at', { ascending: false })

    if (error) {
      console.error('Error fetching project files:', error)
      throw new Error(`Failed to fetch files: ${error.message}`)
    }

    const files = data.map(mapFileRowToProjectFile)

    // Optionally add download URLs
    if (includeDownloadUrls) {
      const filesWithUrls = await Promise.all(
        files.map(async (file) => {
          try {
            const { signedUrl } = await this.storageService.getSignedDownloadUrl(file.filePath)
            return { ...file, downloadUrl: signedUrl }
          } catch (error) {
            console.warn(`Failed to get download URL for ${file.name}:`, error)
            return file
          }
        })
      )
      return filesWithUrls
    }

    return files
  }

  /**
   * Get download URL for a specific file
   */
  async getFileDownloadUrl(fileId: string): Promise<string> {
    const file = await this.getFileById(fileId)
    const { signedUrl } = await this.storageService.getSignedDownloadUrl(file.filePath)
    return signedUrl
  }

  /**
   * Get file content for parsing - auto-detects XLSX files and returns ArrayBuffer, CSV files return string
   */
  async getFileContentForParsing(fileId: string): Promise<{ content: string | ArrayBuffer; fileName: string }> {
    try {
      // Get file metadata
      const file = await this.getFileById(fileId)
      
      // Auto-detect file type by extension
      const isXLSX = file.name.toLowerCase().endsWith('.xlsx')
      
      // Get appropriate content type from storage
      let content: string | ArrayBuffer
      if (isXLSX) {
        console.log(`📊 FileService: Detected XLSX file, using binary download - ${file.name}`)
        content = await this.storageService.getFileContentBinary(file.filePath)
      } else {
        console.log(`📄 FileService: Detected text file, using text download - ${file.name}`)
        content = await this.storageService.getFileContent(file.filePath)
      }
      
      return {
        content,
        fileName: file.name
      }
    } catch (error) {
      console.error('Failed to get file content for parsing:', error)
      throw new Error(`Failed to get file content: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Delete file: both storage and database record
   */
  async deleteFile(fileId: string): Promise<void> {
    try {
      // 1. Get file info first
      const file = await this.getFileById(fileId)

      // 2. Delete from storage
      await this.storageService.deleteFile(file.filePath)

      // 3. Delete from database
      await this.deleteFileRecord(fileId)

    } catch (error) {
      console.error('File deletion failed:', error)
      throw error instanceof Error ? error : new Error('Delete failed')
    }
  }

  /**
   * Check if file exists in both storage and database
   */
  async fileExists(fileId: string): Promise<{ storage: boolean; database: boolean }> {
    try {
      const file = await this.getFileById(fileId)
      const storageExists = await this.storageService.fileExists(file.filePath)
      
      return {
        storage: storageExists,
        database: true // If we got the file record, it exists in DB
      }
    } catch {
      return {
        storage: false,
        database: false
      }
    }
  }

  /**
   * Get file by ID
   */
  private async getFileById(fileId: string): Promise<ProjectFile> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const { data, error } = await supabase
      .from('files')
      .select('*')
      .eq('id', fileId)
      .single()

    if (error || !data) {
      throw new Error(`File not found: ${fileId}`)
    }

    return mapFileRowToProjectFile(data)
  }

  /**
   * Get project by ID
   */
  private async getProjectById(projectId: string): Promise<{ organization_id: string }> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const { data, error } = await supabase
      .from('projects')
      .select('organization_id')
      .eq('id', projectId)
      .single()

    if (error || !data) {
      throw new Error(`Project not found: ${projectId}`)
    }

    return data
  }

  /**
   * Create file record in database
   */
  private async createFileRecord(
    file: Omit<ProjectFile, 'id' | 'uploadedAt' | 'downloadUrl'>
  ): Promise<ProjectFile> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const fileInsert: FileInsert = {
      name: file.name,
      original_name: file.originalName,
      project_id: file.projectId,
      organization_id: file.organizationId,
      file_path: file.filePath,
      file_size: file.fileSize,
      uploaded_by: file.uploadedBy,
      metadata: (file.metadata as Database['public']['Tables']['files']['Insert']['metadata']) || null
    }

    const { data, error } = await supabase
      .from('files')
      .insert(fileInsert)
      .select()
      .single()

    if (error) {
      console.error('Error creating file record:', error)
      throw new Error(`Failed to create file: ${error.message}`)
    }

    return mapFileRowToProjectFile(data)
  }

  /**
   * Delete file record from database
   */
  private async deleteFileRecord(fileId: string): Promise<void> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const { error } = await supabase
      .from('files')
      .delete()
      .eq('id', fileId)

    if (error) {
      console.error('Error deleting file record:', error)
      throw new Error(`Failed to delete file: ${error.message}`)
    }
  }

  /**
   * Update file metadata
   */
  private async updateFileMetadata(fileId: string, metadata: unknown): Promise<void> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const { error } = await supabase
      .from('files')
      .update({ metadata: metadata as Database['public']['Tables']['files']['Update']['metadata'] })
      .eq('id', fileId)

    if (error) {
      console.error('Error updating file metadata:', error)
      throw new Error(`Failed to update file metadata: ${error.message}`)
    }
  }

  /**
   * Overwrite existing file (Option B implementation)
   */
  private async overwriteExistingFile(
    newFile: File, 
    existingFile: ProjectFile
  ): Promise<{ success: boolean; file?: ProjectFile; message?: string }> {
    try {
      // 1. Delete old storage file
      await this.storageService.deleteFile(existingFile.filePath)
      
      // 2. Upload new file to new storage path
      const project = await this.getProjectById(existingFile.projectId)
      const newStoragePath = StorageService.generateStoragePath(existingFile.projectId, newFile.name, project.organization_id)
      const uploadResult = await this.storageService.uploadFile(newFile, newStoragePath)
      
      // 3. Update database record (keep same ID)
      const contentHash = await this.calculateFileHash(newFile)
      const supabase = createClerkSupabaseClient(this.sessionProps)
      
      const { data, error } = await supabase
        .from('files')
        .update({
          file_path: uploadResult.path,
          file_size: newFile.size,
          metadata: {
            storageId: uploadResult.id,
            fullPath: uploadResult.fullPath,
            contentHash,
            uploadedAt: new Date().toISOString(),
            overwrittenAt: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', existingFile.id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update file record: ${error.message}`)
      }

      return { 
        success: true, 
        file: mapFileRowToProjectFile(data),
        message: `File "${newFile.name}" has been updated`
      }

    } catch (error) {
      console.error('File overwrite failed:', error)
      throw error instanceof Error ? error : new Error('Overwrite failed')
    }
  }
} 